/**
 * Resolve which (doctor_id, clinic_id) pair applies for a Firebase-signed-in dashboard user.
 * Staff doctors have doctors/{id}.auth_uid === firebaseUid; assistants may lack that and fall back to legacy linking.
 */
import {
  DocumentData,
  DocumentSnapshot,
  Firestore,
  doc,
  getDoc,
} from 'firebase/firestore';

export function normalizeId(v: string | undefined | null): string {
  return (v ?? '').trim();
}

export function firestoreTruthyDisabled(disabled: unknown): boolean {
  if (disabled === true || disabled === 1) return true;
  if (typeof disabled === 'string')
    return disabled.toLowerCase() === 'true' || disabled === '1';
  return false;
}

function sortAssignments(
  docs: DocumentSnapshot<DocumentData>[],
  preferredClinicId: string | null
): DocumentSnapshot<DocumentData>[] {
  return [...docs].sort((a, b) => {
    const da = a.data() ?? {};
    const dbb = b.data() ?? {};
    if (preferredClinicId) {
      const aMatch = normalizeId(da.clinic_id) === preferredClinicId;
      const bMatch = normalizeId(dbb.clinic_id) === preferredClinicId;
      if (aMatch !== bMatch) return aMatch ? -1 : 1;
    }
    const c = normalizeId(da.clinic_id).localeCompare(normalizeId(dbb.clinic_id));
    if (c !== 0) return c;
    return normalizeId(da.doctor_id).localeCompare(normalizeId(dbb.doctor_id));
  });
}

async function legacyPreferOwnerPick(
  db: Firestore,
  sortedDocs: DocumentSnapshot<DocumentData>[]
): Promise<{ doctorId: string; clinicId: string } | null> {
  if (sortedDocs.length === 0) return null;

  const firstData = sortedDocs[0].data() ?? {};

  if (sortedDocs.length > 1) {
    const firstClinicId = normalizeId(firstData.clinic_id);
    const sameClinicEntries = sortedDocs
      .filter((d) => normalizeId(d.data()?.clinic_id) === firstClinicId)
      .map((d) => ({ snap: d, doctorId: normalizeId(d.data()?.doctor_id) }))
      .filter((e) => !!e.doctorId);

    const clinicSnap = firstClinicId
      ? await getDoc(doc(db, 'clinics', firstClinicId))
      : null;
    const explicitOwnerId = clinicSnap?.exists()
      ? normalizeId(clinicSnap.data()?.owner_doctor_id as string | undefined)
      : '';
    if (explicitOwnerId) {
      const explicitEntry = sameClinicEntries.find((entry) => entry.doctorId === explicitOwnerId);
      if (explicitEntry) return { doctorId: explicitOwnerId, clinicId: firstClinicId };
    }

    // Fetch all doctor docs in parallel instead of sequentially
    const doctorSnaps = await Promise.all(
      sameClinicEntries.map((e) => getDoc(doc(db, 'doctors', e.doctorId)))
    );

    for (let i = 0; i < sameClinicEntries.length; i++) {
      const doctorSnap = doctorSnaps[i];
      const isOwner = doctorSnap.exists() ? doctorSnap.data()?.is_owner : undefined;
      if (isOwner === true || isOwner === undefined) {
        const data = sameClinicEntries[i].snap.data() ?? {};
        const doctorId = normalizeId(data.doctor_id);
        const clinicId = normalizeId(data.clinic_id);
        if (!doctorId) continue;
        return { doctorId, clinicId };
      }
    }
  }

  const doctorId = normalizeId(firstData.doctor_id);
  const clinicId = normalizeId(firstData.clinic_id);
  if (!doctorId) return null;
  return { doctorId, clinicId };
}

export async function resolveAssistantDoctorAssignment(
  db: Firestore,
  assignmentDocs: DocumentSnapshot<DocumentData>[],
  firebaseUid: string,
  preferredClinicId: string | null
): Promise<{ doctorId: string; clinicId: string } | null> {
  if (assignmentDocs.length === 0) return null;

  const uid = normalizeId(firebaseUid);
  const sorted = sortAssignments(assignmentDocs, preferredClinicId);

  // Build entries with unique doctor IDs to avoid duplicate Firestore reads
  const seen = new Set<string>();
  const entries = sorted
    .map((d) => ({
      assignmentDoc: d,
      doctorId: normalizeId(d.data()?.doctor_id),
      clinicId: normalizeId(d.data()?.clinic_id),
    }))
    .filter((e) => {
      if (!e.doctorId || seen.has(e.doctorId)) return false;
      seen.add(e.doctorId);
      return true;
    });

  if (entries.length === 0) return legacyPreferOwnerPick(db, sorted);

  // Fetch all doctor docs in parallel — O(1) round-trip instead of O(n) sequential
  const doctorSnaps = await Promise.all(
    entries.map((e) => getDoc(doc(db, 'doctors', e.doctorId)))
  );

  // Find the doctor whose auth_uid matches the signed-in Firebase UID
  for (let i = 0; i < entries.length; i++) {
    const snap = doctorSnaps[i];
    if (!snap.exists()) continue;
    const authUid = normalizeId(snap.data()?.auth_uid as string | undefined);
    if (authUid && authUid === uid) {
      return { doctorId: entries[i].doctorId, clinicId: entries[i].clinicId };
    }
  }

  return legacyPreferOwnerPick(db, sorted);
}
